import React from 'react';
import _get from 'lodash/get';
import useSyncInfo from 'helpers/useSyncInfo';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import SyncDetailsModal from 'molecules/dataSync/SyncDetailsModal';

import { SyncStatus } from 'types/Source';
import { Theme } from 'styles/ThemeTyping';

interface Props {
  syncStatus: SyncStatus;
  open: boolean;
  onClose: () => void;
}

const SYNC_DATA_LOOKUP = [
  { displayName: 'created profiles', lookupKey: 'createdCount' },
  { displayName: 'updated profiles', lookupKey: 'updatedCount' },
  { displayName: 'deactivated profiles', lookupKey: 'deactivatedCount' },
  { displayName: 'reactivated profiles', lookupKey: 'reactivatedCount' },
  { displayName: 'unique identifier/email duplicates', lookupKey: 'collisionCount' },
];

const PAGE_SYNC_DATA_LOOKUP = [
  { displayName: 'created pages', lookupKey: 'createdCount' },
  { displayName: 'updated pages', lookupKey: 'updatedCount' },
  { displayName: 'deactivated pages', lookupKey: 'deactivatedCount' },
  { displayName: 'Name Collisions', lookupKey: 'collisionCount' },
];

function SyncDetails({ syncStatus, open, onClose }: Props) {
  const classes = useStyles();
  const { hasSyncErrors, lastSyncDate, lastSyncInfo, errorMessage } = useSyncInfo(syncStatus);
  const isPagesRoute = window.location.pathname.includes('/pages/');
  const syncDataLookup = isPagesRoute ? PAGE_SYNC_DATA_LOOKUP : SYNC_DATA_LOOKUP;

  return (
    <SyncDetailsModal
      open={open}
      onClose={onClose}
      title={hasSyncErrors ? 'Error Report' : 'Sync Log'}
      caption={`${hasSyncErrors ? 'Errored at' : 'Last Sync'}: ${lastSyncDate}`}
      header={hasSyncErrors ? 'Summary of Error:' : 'Summary of Sync:'}
      status={hasSyncErrors ? 'error' : 'success'}
    >
      {hasSyncErrors ? (
        <Typography className={classes.errorMessage}>{errorMessage}</Typography>
      ) : (
        <>
          <Table className={classes.modalTable} size="small">
            <TableBody>
              {syncDataLookup.map((item, i) => (
                <TableRow key={i}>
                  <TableCell className={classes.titleCell}>{item.displayName}: </TableCell>
                  <TableCell className={classes.valueCell}>
                    {_get(lastSyncInfo, ['counts', `${item.lookupKey}`])}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography className={classes.syncLogLink}>
            To learn more about your sync summary,{' '}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href="http://help.justsift.com/en/articles/8390251-about-the-data-sync-summary"
              color="secondary"
              className={classes.link}
            >
              see our documentation
            </Link>
            .
          </Typography>
        </>
      )}
    </SyncDetailsModal>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  modalTable: {
    maxHeight: 183,
    marginBottom: theme.spacing(2),
  },
  titleCell: {
    border: 'none',
    paddingBottom: 0,
    paddingLeft: 0,
    textTransform: 'capitalize',
    fontSize: theme.typography.body1.fontSize,
  },
  valueCell: {
    border: 'none',
    paddingBottom: 0,
    paddingRight: 0,
    fontSize: theme.typography.body1.fontSize,
    '&:last-child': {
      paddingRight: 0,
    },
  },
  syncLogLink: {
    marginBottom: theme.spacing(),
  },
  link: {
    cursor: 'pointer',
  },
  errorMessage: {
    marginBottom: theme.spacing(2),
  },
}));

export default SyncDetails;
